import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Download, Printer, Eye, FileText, Calendar, User, Award, TrendingUp } from 'lucide-react';

const ReportCardViewer = ({ 
  reportCard = null,
  onDownload,
  onPrint,
  showPreview = true
}) => {
  const [viewMode, setViewMode] = React.useState('full'); // full, summary

  if (!reportCard) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Report Card</h3>
          <p className="text-gray-600">Report card will appear here once it's generated.</p>
        </CardContent>
      </Card>
    );
  }

  const getGradeColor = (grade) => {
    if (grade >= 90) return 'bg-green-100 text-green-800 border-green-200';
    if (grade >= 80) return 'bg-blue-100 text-blue-800 border-blue-200';
    if (grade >= 70) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    if (grade >= 60) return 'bg-orange-100 text-orange-800 border-orange-200';
    if (grade >= 50) return 'bg-purple-100 text-purple-800 border-purple-200';
    return 'bg-red-100 text-red-800 border-red-200';
  };

  const getGPAColor = (gpa) => {
    if (gpa >= 3.5) return 'text-green-600';
    if (gpa >= 3.0) return 'text-blue-600';
    if (gpa >= 2.5) return 'text-yellow-600';
    if (gpa >= 2.0) return 'text-orange-600';
    return 'text-red-600';
  };

  const getAttendanceColor = (percentage) => {
    if (percentage >= 95) return 'text-green-600';
    if (percentage >= 90) return 'text-blue-600';
    if (percentage >= 80) return 'text-yellow-600';
    if (percentage >= 70) return 'text-orange-600';
    return 'text-red-600';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Report Card</h3>
          <p className="text-sm text-gray-600">
            {reportCard.academicInfo.academicYear} • {reportCard.academicInfo.term}
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === 'full' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('full')}
          >
            <Eye className="w-4 h-4 mr-2" />
            Full View
          </Button>
          <Button
            variant={viewMode === 'summary' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('summary')}
          >
            <FileText className="w-4 h-4 mr-2" />
            Summary
          </Button>
          {onDownload && (
            <Button variant="outline" size="sm" onClick={onDownload}>
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
          )}
          {onPrint && (
            <Button variant="outline" size="sm" onClick={onPrint}>
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
          )}
        </div>
      </div>

      {/* Student Information */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <User className="w-8 h-8 text-blue-600" />
            </div>
            <div className="flex-1">
              <h4 className="text-xl font-semibold">
                {reportCard.student.firstName} {reportCard.student.lastName}
              </h4>
              <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                <span>Admission No: {reportCard.student.admissionNumber}</span>
                <span>Class: {reportCard.academicInfo.className}</span>
                <span>Academic Year: {reportCard.academicInfo.academicYear}</span>
                <span>Term: {reportCard.academicInfo.term}</span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-600">Generated</div>
              <div className="text-sm font-medium">
                {new Date(reportCard.generatedAt).toLocaleDateString()}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">
              {reportCard.gpa.term.toFixed(2)}
            </div>
            <p className="text-sm text-gray-600">Term GPA</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-green-600">
              {reportCard.summary.averageGrade.toFixed(1)}%
            </div>
            <p className="text-sm text-gray-600">Average Grade</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-purple-600">
              {reportCard.summary.passedSubjects}/{reportCard.summary.totalSubjects}
            </div>
            <p className="text-sm text-gray-600">Subjects Passed</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 text-center">
            <div className={`text-2xl font-bold ${getAttendanceColor(reportCard.summary.attendancePercentage || 0)}`}>
              {reportCard.summary.attendancePercentage?.toFixed(1) || 'N/A'}%
            </div>
            <p className="text-sm text-gray-600">Attendance</p>
          </CardContent>
        </Card>
      </div>

      {/* Rankings */}
      {reportCard.rankings && (
        <Card>
          <CardContent className="p-6">
            <h4 className="font-semibold mb-4 flex items-center gap-2">
              <Award className="w-5 h-5" />
              Academic Rankings
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h5 className="font-medium mb-3">Overall Performance</h5>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Class Rank</span>
                    <Badge className="bg-blue-100 text-blue-800">
                      {reportCard.rankings.overall?.rank || 'N/A'} of {reportCard.rankings.overall?.totalStudents || 'N/A'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">GPA</span>
                    <span className={`font-medium ${getGPAColor(reportCard.gpa.term)}`}>
                      {reportCard.gpa.term.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
              
              <div>
                <h5 className="font-medium mb-3">Subject Rankings</h5>
                <div className="space-y-2">
                  {reportCard.rankings.subjects?.slice(0, 3).map((subject, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">{subject.subjectName}</span>
                      <Badge variant="outline">
                        {subject.rank} of {subject.totalStudents}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Grades Table */}
      {viewMode === 'full' && (
        <Card>
          <CardContent className="p-6">
            <h4 className="font-semibold mb-4">Subject Grades</h4>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Subject</th>
                    <th className="text-center py-3 px-4 font-medium text-gray-700">Percentage</th>
                    <th className="text-center py-3 px-4 font-medium text-gray-700">Grade</th>
                    <th className="text-center py-3 px-4 font-medium text-gray-700">Marks</th>
                    <th className="text-center py-3 px-4 font-medium text-gray-700">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {reportCard.grades.map((grade, index) => (
                    <tr key={index} className="border-b border-gray-100">
                      <td className="py-3 px-4 font-medium">{grade.subjectName}</td>
                      <td className="py-3 px-4 text-center">
                        <span className="font-medium">{grade.percentage.toFixed(1)}%</span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <Badge className={getGradeColor(grade.percentage)}>
                          {grade.grade}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-center text-sm text-gray-600">
                        {grade.obtainedMarks.toFixed(1)} / {grade.totalMarks.toFixed(1)}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <Badge variant={grade.percentage >= 50 ? 'default' : 'destructive'}>
                          {grade.percentage >= 50 ? 'Pass' : 'Fail'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Attendance */}
      {reportCard.attendance && (
        <Card>
          <CardContent className="p-6">
            <h4 className="font-semibold mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Attendance Summary
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {reportCard.attendance.totalDays}
                </div>
                <p className="text-sm text-gray-600">Total Days</p>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {reportCard.attendance.presentDays}
                </div>
                <p className="text-sm text-gray-600">Present Days</p>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">
                  {reportCard.attendance.absentDays}
                </div>
                <p className="text-sm text-gray-600">Absent Days</p>
              </div>
              
              <div className="text-center">
                <div className={`text-2xl font-bold ${getAttendanceColor(reportCard.attendance.percentage)}`}>
                  {reportCard.attendance.percentage.toFixed(1)}%
                </div>
                <p className="text-sm text-gray-600">Attendance %</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Performance Summary */}
      <Card>
        <CardContent className="p-6">
          <h4 className="font-semibold mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Performance Summary
          </h4>
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h5 className="font-medium mb-2">Academic Performance</h5>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Subjects:</span>
                    <span className="font-medium">{reportCard.summary.totalSubjects}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Passed Subjects:</span>
                    <span className="font-medium text-green-600">{reportCard.summary.passedSubjects}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Failed Subjects:</span>
                    <span className="font-medium text-red-600">{reportCard.summary.failedSubjects}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Average Grade:</span>
                    <span className="font-medium">{reportCard.summary.averageGrade.toFixed(1)}%</span>
                  </div>
                </div>
              </div>
              
              <div>
                <h5 className="font-medium mb-2">GPA Information</h5>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Term GPA:</span>
                    <span className={`font-medium ${getGPAColor(reportCard.gpa.term)}`}>
                      {reportCard.gpa.term.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Cumulative GPA:</span>
                    <span className={`font-medium ${getGPAColor(reportCard.gpa.cumulative)}`}>
                      {reportCard.gpa.cumulative.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReportCardViewer;
